/**
 * SessionEndScreen - Shown after video session ends
 *
 * Features:
 * - Session summary (date, duration, examiner)
 * - Star rating feedback (1-5)
 * - Optional comment
 * - Shared documents list
 * - Contact information
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import { useMutation } from "convex/react";
import { api } from "@jourfx/convex/client";
import { Button } from "@jourfx/ui";
import {
  CheckCircle,
  Star,
  Download,
  Mail,
  FileText,
  User,
} from "lucide-react";
import { format } from "date-fns";
import { de, enUS } from "date-fns/locale";

// Type-only definition for Convex Id type
type Id<T extends string> = string & { __tableName: T };

interface SessionEndScreenProps {
  appointmentId: string;
  sessionDuration: number; // in seconds
  examinerName?: string;
  examinerAvatarUrl?: string;
  appointmentDate?: Date;
  feedbackEnabled?: boolean;
}

function StarRating({
  value,
  onChange,
}: {
  value: number;
  onChange: (rating: number) => void;
}) {
  const [hovered, setHovered] = useState(0);

  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          onMouseEnter={() => setHovered(star)}
          onMouseLeave={() => setHovered(0)}
          className="p-1 transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-[var(--sage-500)] rounded"
          aria-label={`${star} stars`}
        >
          <Star
            className={`w-8 h-8 transition-colors ${
              star <= (hovered || value)
                ? "fill-yellow-400 text-yellow-400"
                : "text-[var(--stone-300)]"
            }`}
          />
        </button>
      ))}
    </div>
  );
}

export function SessionEndScreen({
  appointmentId,
  sessionDuration,
  examinerName = "Dr. Schmidt",
  examinerAvatarUrl,
  appointmentDate = new Date(),
  feedbackEnabled = true,
}: SessionEndScreenProps) {
  const { t, i18n } = useTranslation("video");
  const navigate = useNavigate();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

  const apiRef = api as any;
  const submitFeedback = useMutation(apiRef.feedback.index.submit);

  // Placeholder for shared documents (not implemented yet)
  const documents: Array<{ _id: string; name: string; storageKey: string }> =
    [];

  const locale = i18n.language === "de" ? de : enUS;
  const durationMinutes = Math.ceil(sessionDuration / 60);

  const handleSubmitFeedback = async () => {
    if (rating === 0) return;

    setIsSubmitting(true);
    try {
      await submitFeedback({
        appointmentId: appointmentId as Id<"appointments">,
        rating,
        comment: comment || undefined,
        sessionDuration,
      });
      setFeedbackSubmitted(true);
    } catch (err) {
      console.error("Failed to submit feedback:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDone = () => {
    navigate({ to: "/" });
  };

  return (
    <div className="min-h-screen bg-[var(--stone-50)] flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8 space-y-6">
        {/* Success header */}
        <div className="text-center">
          <div className="w-16 h-16 bg-[var(--sage-100)] rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-[var(--sage-600)]" />
          </div>
          <h1 className="text-2xl font-semibold text-[var(--stone-900)]">
            {t("sessionEnd.title")}
          </h1>
        </div>

        {/* Session summary */}
        <div className="bg-[var(--stone-50)] rounded-xl p-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-[var(--stone-500)]">
              {t("sessionEnd.summary.date")}
            </span>
            <span className="font-medium text-[var(--stone-900)]">
              {format(appointmentDate, "PPP", { locale })}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-[var(--stone-500)]">
              {t("sessionEnd.summary.duration")}
            </span>
            <span className="font-medium text-[var(--stone-900)]">
              {durationMinutes} {t("sessionEnd.summary.minutes")}
            </span>
          </div>
          <div className="flex justify-between items-center text-sm">
            <span className="text-[var(--stone-500)]">
              {t("sessionEnd.summary.examiner")}
            </span>
            <span className="font-medium text-[var(--stone-900)] flex items-center gap-2">
              {examinerAvatarUrl ? (
                <img src={examinerAvatarUrl} alt="" className="w-6 h-6 rounded-full object-cover" />
              ) : (
                <span className="w-6 h-6 rounded-full bg-[var(--sage-100)] flex items-center justify-center">
                  <User className="w-3.5 h-3.5 text-[var(--sage-600)]" />
                </span>
              )}
              {examinerName}
            </span>
          </div>
        </div>

        {/* Feedback section (only when enabled) */}
        {feedbackEnabled ? (
          !feedbackSubmitted ? (
            <div className="space-y-4">
              <h2 className="text-lg font-medium text-[var(--stone-900)] text-center">
                {t("sessionEnd.feedback.title")}
              </h2>
              <div className="flex justify-center">
                <StarRating value={rating} onChange={setRating} />
              </div>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={t("sessionEnd.feedback.commentPlaceholder")}
                className="w-full h-24 px-3 py-2 rounded-lg border border-[var(--stone-200)]
                           focus:outline-none focus:ring-2 focus:ring-[var(--sage-500)]
                           resize-none text-sm"
              />
              <div className="flex gap-3">
                <Button
                  variant="secondary"
                  size="client"
                  className="flex-1"
                  onClick={handleDone}
                >
                  {t("sessionEnd.feedback.skip")}
                </Button>
                <Button
                  variant="default"
                  size="client"
                  className="flex-1"
                  onClick={handleSubmitFeedback}
                  disabled={rating === 0 || isSubmitting}
                >
                  {isSubmitting ? "..." : t("sessionEnd.feedback.submit")}
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-[var(--sage-600)] font-medium">
                {t("sessionEnd.feedback.thankYou")}
              </p>
            </div>
          )
        ) : (
          /* Simple done button when feedback is disabled */
          <Button
            variant="default"
            size="client"
            className="w-full"
            onClick={handleDone}
          >
            {t("sessionEnd.done")}
          </Button>
        )}

        {/* Documents section */}
        {documents && documents.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-[var(--stone-700)] flex items-center gap-2">
              <FileText className="w-4 h-4" />
              {t("sessionEnd.documents.title")}
            </h3>
            <div className="space-y-2">
              {documents.map((doc) => (
                <div
                  key={doc._id}
                  className="flex items-center justify-between p-3 bg-[var(--stone-50)] rounded-lg"
                >
                  <span className="text-sm text-[var(--stone-700)] truncate">
                    {doc.name}
                  </span>
                  <Button variant="ghost" size="sm">
                    <Download className="w-4 h-4" />
                    {t("sessionEnd.documents.download")}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Contact info */}
        <div className="border-t border-[var(--stone-200)] pt-4 space-y-2">
          <h3 className="text-sm font-medium text-[var(--stone-500)]">
            {t("sessionEnd.contact.title")}
          </h3>
          <div className="flex flex-col gap-1 text-sm text-[var(--stone-600)]">
            <a
              href="mailto:support@jourfx.app"
              className="flex items-center gap-2 hover:text-[var(--sage-600)]"
            >
              <Mail className="w-4 h-4" />
              support@jourfx.app
            </a>
          </div>
        </div>

        {/* Done button (shown after feedback submitted) */}
        {feedbackSubmitted && (
          <Button
            variant="default"
            size="client"
            className="w-full"
            onClick={handleDone}
          >
            {t("sessionEnd.done")}
          </Button>
        )}
      </div>
    </div>
  );
}
